import type { TrainingErrorCode, TrainingResult } from './training.ts';
export type NutritionResult<T> = TrainingResult<T>;
export type NutritionErrorCode = TrainingErrorCode;
export type MealResolution = 'REALIZADA' | 'OMITIDA';
export type MealStatus = 'SIN_REGISTRAR' | MealResolution;
export type NutritionCoverage = 'COMPLETA' | 'PARCIAL' | 'NO_CALCULABLE';
export type ShoppingStatus = 'PENDIENTE' | 'COMPRADO';
export interface Quantity { value:number; unit:string; basis?:string; equivalence?:{ grams:number; context:string } }
// Retained only for legacy records created before CompositionSnapshot existed.
// M-06 never treats these bare values as identified composition.
export interface Nutrients { energyKcal?:number; proteinG?:number; carbohydrateG?:number; fatG?:number }
export type NutrientSnapshot = { state:'UNKNOWN' } | { state:'KNOWN'; value:number };
export interface CompositionSource {
  label:string;
  observedAt:string;
  externalReference?:string;
  versionOrEdition?:string;
}
export interface CompositionReferenceBasis {
  amount:number;
  unit:string;
  preparationBase:string;
}
export interface CompositionNutrients {
  energyKcal:NutrientSnapshot;
  proteinG:NutrientSnapshot;
  carbohydrateG:NutrientSnapshot;
  fatG:NutrientSnapshot;
}
export type CompositionSnapshot =
  | { state:'UNKNOWN' }
  | { state:'KNOWN'; source:CompositionSource; reference:CompositionReferenceBasis; nutrients:CompositionNutrients };
export interface FoodEntry { name:string; quantity?:Quantity; nutrients?:Nutrients; composition?:CompositionSnapshot }
export interface Recipe { recipeId:string; currentRevisionRef:string; archived:boolean }
export interface RecipeRevision { recipeId:string; recipeRevisionId:string; revision:number; name:string; ingredients:readonly FoodEntry[] }
export interface PlannedMeal { plannedMealId:string; revision:number; localDate:string; title:string; recipeRevisionRef?:string; contents:readonly FoodEntry[]; trainingSessionId?:string }
export interface Consumption { consumptionId:string; revision:number; localDate:string; resolution:MealResolution; plannedMealId?:string; plannedMealRevision?:number; contents:readonly FoodEntry[] }
export interface ShoppingItem { shoppingItemId:string; revision:number; name:string; quantity?:number; unit?:string; note?:string; status:ShoppingStatus }
const id=/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const validId=(v:unknown):v is string=>typeof v==='string'&&id.test(v);
export const validRevision=(v:unknown):v is number=>Number.isSafeInteger(v)&&Number(v)>=1;
export function validLocalDate(v:unknown):v is string { if(typeof v!=='string'||!/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(v))return false;const [y,m,d]=v.split('-').map(Number);const date=new Date(Date.UTC(y!,m!-1,d));return date.getUTCFullYear()===y&&date.getUTCMonth()===m!-1&&date.getUTCDate()===d; }
const text=(v:unknown)=>typeof v==='string'&&v.trim().length>0;
export function validQuantity(v:unknown):v is Quantity { const q=v as Quantity;return !!q&&Number.isFinite(q.value)&&q.value>=0&&text(q.unit)&&(q.basis===undefined||text(q.basis))&&(q.equivalence===undefined||(!!q.equivalence&&typeof q.equivalence==='object'&&Number.isFinite(q.equivalence.grams)&&q.equivalence.grams>0&&text(q.equivalence.context))); }
function validNutrients(v:unknown):v is Nutrients { if(!v||typeof v!=='object')return false;const n=v as Record<string,unknown>, keys=['energyKcal','proteinG','carbohydrateG','fatG'];return keys.some(k=>n[k]!==undefined)&&keys.every(k=>n[k]===undefined||(typeof n[k]==='number'&&Number.isFinite(n[k])&&Number(n[k])>=0)); }
export function validNutrientSnapshot(v:unknown):v is NutrientSnapshot {
  if(!v||typeof v!=='object')return false;
  const n=v as Record<string,unknown>;
  return (n.state==='UNKNOWN'&&n.value===undefined) || (n.state==='KNOWN'&&typeof n.value==='number'&&Number.isFinite(n.value)&&n.value>=0);
}
export function validCompositionSnapshot(v:unknown):v is CompositionSnapshot {
  if(!v||typeof v!=='object')return false;
  const c=v as any;
  if(c.state==='UNKNOWN')return c.source===undefined&&c.reference===undefined&&c.nutrients===undefined;
  if(c.state!=='KNOWN'||!c.source||!c.reference||!c.nutrients)return false;
  if(!text(c.source.label)||!text(c.source.observedAt)
    ||(c.source.externalReference!==undefined&&!text(c.source.externalReference))
    ||(c.source.versionOrEdition!==undefined&&!text(c.source.versionOrEdition)))return false;
  if(typeof c.reference.amount!=='number'||!Number.isFinite(c.reference.amount)||c.reference.amount<=0
    ||!text(c.reference.unit)||!text(c.reference.preparationBase))return false;
  return ['energyKcal','proteinG','carbohydrateG','fatG']
    .every(key=>validNutrientSnapshot(c.nutrients[key]));
}
export function compositionSnapshot(entry:FoodEntry):CompositionSnapshot {
  return entry.composition ?? {state:'UNKNOWN'};
}
export function validFoodEntry(v:unknown):v is FoodEntry { const f=v as FoodEntry;return !!f&&text(f.name)&&(f.quantity===undefined||validQuantity(f.quantity))&&(f.nutrients===undefined||validNutrients(f.nutrients))&&(f.composition===undefined||validCompositionSnapshot(f.composition)); }
const entries=(v:unknown)=>Array.isArray(v)&&v.every(validFoodEntry);
export function validRecipe(v:unknown):v is Recipe { const r=v as Recipe;return !!r&&validId(r.recipeId)&&validId(r.currentRevisionRef)&&typeof r.archived==='boolean'; }
export function validRecipeRevision(v:unknown):v is RecipeRevision { const r=v as RecipeRevision;return !!r&&validId(r.recipeId)&&validId(r.recipeRevisionId)&&validRevision(r.revision)&&text(r.name)&&entries(r.ingredients); }
export function validPlannedMeal(v:unknown):v is PlannedMeal { const m=v as PlannedMeal;return !!m&&validId(m.plannedMealId)&&validRevision(m.revision)&&validLocalDate(m.localDate)&&text(m.title)&&entries(m.contents)&&(m.recipeRevisionRef===undefined||validId(m.recipeRevisionRef))&&(m.trainingSessionId===undefined||validId(m.trainingSessionId))&&(!!m.recipeRevisionRef||m.contents.length>0); }
export function validConsumption(v:unknown):v is Consumption { const c=v as Consumption;return !!c&&validId(c.consumptionId)&&validRevision(c.revision)&&validLocalDate(c.localDate)&&(c.resolution==='REALIZADA'||c.resolution==='OMITIDA')&&entries(c.contents)&&(c.plannedMealId===undefined?(c.plannedMealRevision===undefined&&c.resolution==='REALIZADA'&&c.contents.length>0):(validId(c.plannedMealId)&&validRevision(c.plannedMealRevision)))&&(c.resolution==='REALIZADA'||c.contents.length===0); }
export function validShoppingItem(v:unknown):v is ShoppingItem { const s=v as ShoppingItem;return !!s&&validId(s.shoppingItemId)&&validRevision(s.revision)&&text(s.name)&&(s.quantity===undefined||(Number.isFinite(s.quantity)&&s.quantity>=0))&&(s.unit===undefined||text(s.unit))&&(s.note===undefined||text(s.note))&&(s.status==='PENDIENTE'||s.status==='COMPRADO'); }
export const failure=<T=never>(code:NutritionErrorCode):NutritionResult<T>=>({ok:false,code});
// Legacy/public helper for explicit contextual equivalences expressed in grams.
// It never assumes a conversion for arbitrary units.
export function quantitativeBasis(quantity: Quantity | undefined): { grams: number; context?:string } | undefined {
  if (!quantity || !validQuantity(quantity)) return undefined;
  if (quantity.unit.trim() === 'g') return { grams: quantity.value, ...(quantity.basis?{context:quantity.basis.trim()}:{}) };
  const equivalence = quantity.equivalence;
  if (!equivalence || (quantity.basis !== undefined && quantity.basis.trim() !== equivalence.context.trim())) return undefined;
  const grams = quantity.value * equivalence.grams;
  return Number.isFinite(grams) ? { grams, context: equivalence.context.trim() } : undefined;
}

// Ratio between the recorded quantity and the immutable CompositionSnapshot
// reference basis. Equal explicit units need no conversion. Contextual units
// are only comparable with a gram reference through their explicit equivalence.
export function compositionReferenceRatio(entry: FoodEntry): number | undefined {
  const quantity = entry.quantity;
  const composition = compositionSnapshot(entry);
  if (!quantity || !validQuantity(quantity) || composition.state !== 'KNOWN') return undefined;
  const quantityBase = quantity.basis?.trim();
  const preparationBase = composition.reference.preparationBase.trim();
  if (!quantityBase || quantityBase !== preparationBase) return undefined;

  const quantityUnit = quantity.unit.trim();
  const referenceUnit = composition.reference.unit.trim();
  let amount: number | undefined;
  if (quantityUnit === referenceUnit) amount = quantity.value;
  else if (referenceUnit === 'g') {
    const explicit = quantitativeBasis(quantity);
    if (explicit?.context === preparationBase) amount = explicit.grams;
  }
  if (amount === undefined) return undefined;
  const ratio = amount / composition.reference.amount;
  return Number.isFinite(ratio) && ratio >= 0 ? ratio : undefined;
}
function componentCoverage(entry:FoodEntry):NutritionCoverage {
  const ratio=compositionReferenceRatio(entry),composition=compositionSnapshot(entry);
  if(ratio===undefined||composition.state!=='KNOWN')return 'NO_CALCULABLE';
  let known=0;
  for(const key of ['energyKcal','proteinG','carbohydrateG','fatG'] as const) {
    if(composition.nutrients[key].state==='KNOWN')known++;
  }
  return known===4?'COMPLETA':known>0?'PARCIAL':'NO_CALCULABLE';
}
export function coverage(entries: readonly FoodEntry[]): NutritionCoverage {
  if(!entries.length)return 'NO_CALCULABLE';
  const states=entries.map(componentCoverage);
  return states.every(x=>x==='COMPLETA')?'COMPLETA':states.some(x=>x!=='NO_CALCULABLE')?'PARCIAL':'NO_CALCULABLE';
}
export function deriveMealStatus(consumption?:Consumption):NutritionResult<MealStatus>{return consumption&&!validConsumption(consumption)?failure('CORRUPTION_DETECTED'):{ok:true,value:consumption?.resolution??'SIN_REGISTRAR'};}
